import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Quiz,
  Assignment,
  School,
  Today,
  CalendarToday,
  AccessTime,
  Analytics,
  Psychology,
  Groups,
  EmojiEvents,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { DashboardStats } from '../services/adminService';

interface AdminOverviewProps {
  stats: DashboardStats | null;
}

const AdminOverview: React.FC<AdminOverviewProps> = ({ stats }) => {
  const theme = useTheme();

  const getSubjectIcon = (subject: string) => {
    switch (subject) {
      case 'math': return '🧮';
      case 'science': return '🔬';
      case 'english': return '📖';
      case 'gk': return '🌍';
      default: return '📚';
    }
  };

  const getSubjectColor = (subject: string) => {
    switch (subject) {
      case 'math': return theme.palette.primary.main;
      case 'science': return theme.palette.secondary.main;
      case 'english': return theme.palette.success.main;
      case 'gk': return theme.palette.warning.main;
      default: return theme.palette.grey[500];
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatCount = (count: number, singular: string, plural: string) => {
    return `${count} ${count === 1 ? singular : plural}`;
  };

  const MetricCard = ({ title, value, change, icon, color, subtitle }: any) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        sx={{
          background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
          border: `1px solid ${alpha(color, 0.1)}`,
          transition: 'all 0.3s ease',
          height: '100%',
          minHeight: 160,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 20px ${alpha(color, 0.15)}`,
          },
        }}
      >
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', height: '100%' }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <Box>
                {typeof value === 'string' || typeof value === 'number' ? (
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color, mb: 1 }}>
                    {value}
                  </Typography>
                ) : (
                  <Box sx={{ mb: 1 }}>
                    {value}
                  </Box>
                )}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {title}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" color="textSecondary">
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {change !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  {change > 0 ? (
                    <TrendingUp sx={{ fontSize: 16, color: theme.palette.success.main, mr: 0.5 }} />
                  ) : change < 0 ? (
                    <TrendingDown sx={{ fontSize: 16, color: theme.palette.error.main, mr: 0.5 }} />
                  ) : null}
                  <Typography
                    variant="body2"
                    sx={{
                      color: change > 0 ? theme.palette.success.main : change < 0 ? theme.palette.error.main : theme.palette.text.secondary,
                      fontWeight: 'medium',
                    }}
                  >
                    {change > 0 ? '+' : ''}{change}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(color, 0.1),
                color,
              }}
            >
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  if (!stats) {
    console.log('⚠️ AdminOverview: No stats data available');
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          Loading overview data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1 }}>
      {/* Main Overview Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: '1fr 1fr 1fr 1fr',
          },
          gap: 3,
          mb: 4,
        }}
      >
        <MetricCard
          title="Total Students"
          value={stats.overview.totalStudents}
          change={stats.growth.studentsWeek}
          icon={<Groups />}
          color={theme.palette.primary.main}
          subtitle="Active learners"
        />
        <MetricCard
          title="Question Bank"
          value={stats.overview.totalQuestions}
          icon={<Quiz />}
          color={theme.palette.secondary.main}
          subtitle="Available questions"
        />
        <MetricCard
          title="Tests Completed"
          value={stats.overview.totalTests}
          change={stats.growth.testsWeek}
          icon={<Assignment />}
          color={theme.palette.success.main}
          subtitle="All time"
        />
        <MetricCard
          title="Average Score"
          value={`${Math.round(stats.overview.avgScore)}%`}
          icon={<Psychology />}
          color={theme.palette.warning.main}
          subtitle="Platform average"
        />
      </Box>

      {/* Growth Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: '1fr 1fr 1fr 1fr',
          },
          gap: 3,
          mb: 4,
        }}
      >
        <MetricCard
          title="Today's Activity"
          value={
            <>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {formatCount(stats.growth.studentsToday, 'Student', 'Students')}, {formatCount(stats.growth.testsToday, 'Test', 'Tests')}
              </Typography>
            </>
          }
          change={stats.growth.studentsToday}
          icon={<Today />}
          color={theme.palette.success.main}
          subtitle="New registrations and tests"
        />
        <MetricCard
          title="This Week"
          value={
            <>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {formatCount(stats.growth.studentsWeek, 'Student', 'Students')}, {formatCount(stats.growth.testsWeek, 'Test', 'Tests')}
              </Typography>
            </>
          }
          change={stats.growth.studentsWeek}
          icon={<CalendarToday />}
          color={theme.palette.primary.main}
          subtitle="Weekly growth"
        />
        <MetricCard
          title="This Month"
          value={
            <>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {formatCount(stats.growth.studentsMonth, 'Student', 'Students')}, {formatCount(stats.growth.testsMonth, 'Test', 'Tests')}
              </Typography>
            </>
          }
          change={stats.growth.studentsMonth}
          icon={<AccessTime />}
          color={theme.palette.secondary.main}
          subtitle="Monthly performance"
        />
        <MetricCard
          title="This Year"
          value={
            <>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                {formatCount(stats.growth.studentsYear || 0, 'Student', 'Students')}, {formatCount(stats.growth.testsYear || 0, 'Test', 'Tests')}
              </Typography>
            </>
          }
          change={stats.growth.studentsYear || 0}
          icon={<Analytics />}
          color={theme.palette.info.main}
          subtitle="Annual growth"
        />
      </Box>

      {/* Main Content Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '1fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Popular Subjects */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
              <Analytics sx={{ mr: 1 }} />
              Popular Subjects
            </Typography>
            <List>
              {stats.distribution.popularSubjects.map((subject, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: alpha(getSubjectColor(subject._id), 0.1),
                        color: getSubjectColor(subject._id),
                      }}
                    >
                      {getSubjectIcon(subject._id)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={subject._id.charAt(0).toUpperCase() + subject._id.slice(1)}
                    secondary={`${subject.count} tests completed`}
                  />
                  <Box sx={{ minWidth: 80 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(subject.count / stats.overview.totalTests) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: alpha(getSubjectColor(subject._id), 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getSubjectColor(subject._id),
                        },
                      }}
                    />
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        </motion.div>

        {/* Top Performers */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
              <EmojiEvents sx={{ mr: 1 }} />
              Top Performers
            </Typography>
            <List>
              {stats.activity.topPerformers.map((performer, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : theme.palette.grey[300],
                        color: index < 3 ? 'white' : 'black',
                      }}
                    >
                      {index + 1}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={performer.name}
                    secondary={`Grade ${performer.grade} • ${performer.testCount} tests`}
                  />
                  <Chip
                    label={`${performer.avgScore}%`}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main,
                      fontWeight: 'bold',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </motion.div>

        {/* Grade Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
              <School sx={{ mr: 1 }} />
              Grade Distribution
            </Typography>
            <List>
              {stats.distribution.gradeDistribution.map((grade, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    >
                      {grade._id}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`Grade ${grade._id}`}
                    secondary={`${grade.count} students`}
                  />
                  <Box sx={{ minWidth: 80 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(grade.count / stats.overview.totalStudents) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.primary.main,
                        },
                      }}
                    />
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
              <Groups sx={{ mr: 1 }} />
              Recent Test Activity
            </Typography>
            <List>
              {stats.activity.recentTests.map((test, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: alpha(getSubjectColor(test.subject), 0.1),
                        color: getSubjectColor(test.subject),
                      }}
                    >
                      {getSubjectIcon(test.subject)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={test.student.name}
                    secondary={`${test.subject} • Grade ${test.student.grade} • ${formatDateTime(test.endTime)}`}
                  />
                  <Chip
                    label={`${test.score}%`}
                    size="small"
                    sx={{
                      bgcolor: test.score >= 80 ? alpha(theme.palette.success.main, 0.1) : 
                               test.score >= 60 ? alpha(theme.palette.warning.main, 0.1) : 
                               alpha(theme.palette.error.main, 0.1),
                      color: test.score >= 80 ? theme.palette.success.main : 
                             test.score >= 60 ? theme.palette.warning.main : 
                             theme.palette.error.main,
                      fontWeight: 'bold',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </motion.div>
      </Box>
    </Box>
  );
};

export default AdminOverview; 